import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Input, Button, List, Avatar, Badge, Space, Divider, Tabs } from "antd";
import { MessageOutlined, SendOutlined, CheckCircleOutlined, UserOutlined, CustomerServiceOutlined, FileTextOutlined, ReloadOutlined } from "@ant-design/icons";
import { getHistorialSolicitud, addMensaje, cerrarSolicitud, confirmarLecturaComentario, reloadAllSilencioso } from "../../../context/actions/atencionActions";
import { FilesList } from "./FilesList";
import { FileUpload } from "./FileUpload";
import dayjs from "dayjs";

const { TextArea } = Input;

export const ModalChat = ({ visible, onClose, solicitud }) => {
  const dispatch = useDispatch();
  const { 
    historialActual, 
    loading, 
    solicitudActiva, 
    archivosActuales 
  } = useSelector((state) => state.atencionReducer);

  const { user } = useSelector((state) => state.userReducer);
  
  const [nuevoMensaje, setNuevoMensaje] = useState("");
  const [enviandoMensaje, setEnviandoMensaje] = useState(false);
  const [tabActivo, setTabActivo] = useState("mensajes");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (visible && solicitud) {
      dispatch(getHistorialSolicitud(solicitud.proveedorSolicitudID));
    }
  }, [visible, solicitud, dispatch]);

  useEffect(() => {
    scrollToBottom();
  }, [historialActual]);

  // auto-scroll cuando se abre el modal
  useEffect(() => {
    if (visible && historialActual.length > 0) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [visible, historialActual.length]);

  // marcar como leído cuando se abre el chat
  useEffect(() => {
    if (visible && solicitud?.proveedorSolicitudID) {
      dispatch(confirmarLecturaComentario(solicitud.proveedorSolicitudID));
    }
  }, [visible, solicitud?.proveedorSolicitudID, dispatch]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      const container = messagesEndRef.current.closest('.atencion-chat-container-moderno');
      if (container) {
        container.scrollTop = container.scrollHeight;
      } else {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handleEnviarMensaje = async () => {
    if (!nuevoMensaje.trim()) return;

    setEnviandoMensaje(true);
    
    const mensajeData = {
      solicitudID: solicitud.proveedorSolicitudID,
      comentario: nuevoMensaje.trim()
    };

    await dispatch(addMensaje(mensajeData));
    setNuevoMensaje("");
    setEnviandoMensaje(false);
  };

  const handleCerrarSolicitud = () => {
    Modal.confirm({
      title: "¿Cerrar solicitud?",
      content: "Esta acción cerrará la solicitud definitivamente. ¿Deseas continuar?",
      okText: "Sí, cerrar",
      cancelText: "Cancelar",
      onOk: () => {
        dispatch(cerrarSolicitud(solicitud.proveedorSolicitudID));
        onClose();
      }
    });
  };

  const handleRecargarConversacion = () => {
    if (solicitud?.proveedorSolicitudID) {
      dispatch(reloadAllSilencioso(solicitud.proveedorSolicitudID));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEnviarMensaje();
    }
  };

  const esSolicitudCerrada = solicitud?.estatus === "Cerrado";

  // función para determinar si el mensaje es del admin actual
  const esMensajeAdmin = (mensaje) => {
    return mensaje.usuarioNombre?.toLowerCase().includes('admin') || 
           mensaje.usuarioNombre?.toLowerCase().includes('soporte') ||
           mensaje.usuarioNombre === user?.nombreCompleto || 
           mensaje.usuarioNombre === user?.nombre ||
           mensaje.usuarioID === user?.usuarioID;
  };

  // función para renderizar un mensaje individual
  const renderMensaje = (mensaje) => {
    const esAdmin = esMensajeAdmin(mensaje);
    const esPropio = esAdmin;
    
    return (
      <div className={`atencion-mensaje-wrapper-moderno ${esPropio ? 'es-mensaje-propio' : 'es-mensaje-externo'} ${esAdmin ? 'es-mensaje-admin' : ''}`}>
        <div className="atencion-burbuja-chat-moderna">
          <div className="atencion-mensaje-header-unico">
            <div className="atencion-usuario-info-unica">
              <Avatar 
                size="small" 
                icon={esAdmin ? <CustomerServiceOutlined /> : <UserOutlined />}
                style={{ 
                  backgroundColor: esPropio ? '#ff7b00' : esAdmin ? '#52c41a' : '#1890ff'
                }}
              />
              <span className="atencion-usuario-nombre-unico">{mensaje.usuarioNombre}</span>
            </div>
            <div className="atencion-mensaje-tiempo-unico">
              {dayjs(mensaje.fechaCreacion).format("HH:mm")}
            </div>
          </div>
          <div className="atencion-mensaje-contenido-unico">
            {mensaje.comentario}
          </div>
          <div className="atencion-mensaje-footer-unico">
            <div className="atencion-mensaje-fecha-unica">
              {dayjs(mensaje.fechaCreacion).format("DD/MM/YYYY")}
            </div>
            {/* {!mensaje.leido && (
              <Badge 
                status="warning" 
                text="No leído" 
                style={{ fontSize: '11px' }}
              />
            )} */}
          </div>
        </div>
      </div>
    );
  };

  const tabItems = [
    {
      key: 'mensajes',
      label: (
        <Space>
          <MessageOutlined />
          Mensajes
        </Space>
      ),
      children: (
        <div className="mensajes-tab-content">
          {/* lista de mensajes */}
          <div className="atencion-chat-container-moderno" style={{ height: "300px", overflowY: "auto" }}>
            {loading ? (
              <div className="mensaje-cargando">
                Cargando historial...
              </div>
            ) : historialActual.length > 0 ? (
              <div className="atencion-mensajes-lista-nueva">
                {historialActual
                  .sort((a, b) => new Date(a.fechaCreacion) - new Date(b.fechaCreacion))
                  .map((mensaje, index) => (
                    <div key={mensaje.id || index} className="atencion-mensaje-item-unico">
                      {renderMensaje(mensaje)}
                    </div>
                  ))}
              </div>
            ) : (
              <div className="sin-mensajes">
                <MessageOutlined style={{ fontSize: '24px', color: '#ccc', marginBottom: '8px' }} />
                <p>No hay mensajes en esta solicitud</p>
                <p style={{ fontSize: '12px', color: '#999' }}>Escribe el primer mensaje para comenzar la conversación</p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* input para nuevo mensaje (solo si no está cerrada) */}
          {!esSolicitudCerrada && (
            <>
              <Divider />
              <div className="chat-input-container">
                <div className="chat-input-wrapper">
                  <FileUpload solicitudId={solicitud?.proveedorSolicitudID} compact />
                  <div className="text-input-container">
                    <Input
                      placeholder="Escribe tu mensaje..."
                      value={nuevoMensaje}
                      onChange={(e) => setNuevoMensaje(e.target.value)}
                      onPressEnter={handleEnviarMensaje}
                      disabled={enviandoMensaje}
                      className="custom-message-input"
                    />
                  </div>
                  <div 
                    className={`custom-send-btn ${!nuevoMensaje.trim() ? 'disabled' : ''} ${enviandoMensaje ? 'loading' : ''}`}
                    onClick={!enviandoMensaje && nuevoMensaje.trim() ? handleEnviarMensaje : undefined}
                    title={enviandoMensaje ? "Enviando..." : !nuevoMensaje.trim() ? "Escribe un mensaje" : "Enviar mensaje"}
                  >
                    <SendOutlined />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )
    },
    {
      key: 'archivos',
      label: (
        <Space>
          <FileTextOutlined />
          Archivos
          {archivosActuales.length > 0 && (
            <Badge count={archivosActuales.length} size="small" />
          )}
        </Space>
      ),
      children: (
        <div className="archivos-tab-content" style={{ height: "380px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <FilesList solicitud={solicitud} />
        </div>
      )
    }
  ];

  return (
    <Modal
      title={
        <Space>
          <MessageOutlined style={{ color: "var(--color-naranja)" }} />
          <span>Solicitud #{solicitud?.proveedorSolicitudID}</span>
          <Badge 
            status={esSolicitudCerrada ? "default" : "processing"} 
            text={solicitud?.estatus || "Abierto"}
            className={solicitud?.estatus === "Abierto" ? "badge-abierto" : ""}
          />
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={null}
      className="modal-chat"
      style={{ top: 20 }}
    >
      {solicitud && (
        <>
          {/* header con información de la solicitud */}
          <div className="chat-header">
            <div className="solicitud-info">
              <p><strong>Descripción inicial:</strong> {solicitud.comentario}</p>
              <p><strong>Fecha:</strong> {dayjs(solicitud.fechaCreacion).format("DD/MM/YYYY HH:mm")}</p>
              <p><strong>Proveedor:</strong> {solicitud.proveedorNombre}</p>
              <p><strong>Usuario:</strong> {solicitud.proveedorUsuarioNombre}</p>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {!esSolicitudCerrada && (
                <Button 
                  icon={<CheckCircleOutlined />}
                  onClick={handleCerrarSolicitud}
                  size="default"
                  style={{ 
                    borderColor: '#52c41a',
                    color: '#52c41a',
                    backgroundColor: '#fff',
                    minWidth: '140px',
                    borderRadius: '6px'
                  }}
                >
                  Cerrar Solicitud
                </Button>
              )}
              <Button 
                type="default"
                icon={<ReloadOutlined />}
                onClick={handleRecargarConversacion}
                size="default"
                style={{ 
                  borderColor: '#1890ff',
                  color: '#1890ff',
                  minWidth: '140px',
                  borderRadius: '6px'
                }}
              >
                Recargar
              </Button>
            </div>
          </div>

          {/* tabs para Mensajes y Archivos */}
          <Tabs
            activeKey={tabActivo}
            onChange={setTabActivo}
            items={tabItems}
            className="chat-tabs"
          />
        </>
      )}
      
    </Modal>
  );
};